import React from 'react';
import { AppBar, Toolbar } from '@mui/material';
import { Button } from '@customink/pigment-react';
import Spinner from '~/components/Spinner';
import { styled } from '@customink/pigment-react/lib/themeCustomink';
import { FileError, FileRejection } from 'react-dropzone';
import { ACCEPTED_FILE_TYPES, useUpload, FILE_MAX_SIZE_IN_BYTES, isFileTypeAccepted } from './useUpload';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

// On mobile devices, when a button is disabled, it can still receive focus
// which triggers hover styles. Since hover styles take precedence over disabled styles,
// the button appears enabled despite being disabled. This override ensures the button
// maintains its disabled appearance.
const StyledButton = styled(Button)(({ theme }) => ({
    '&.Mui-disabled': {
        '&:hover': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
        },
    },
}));

const MobileUpload: React.FC = () => {
    const { onUpload, isPending } = useUpload();
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const acceptedFiles: File[] = [];
        const rejectedFiles: FileRejection[] = [];

        files.forEach((file) => {
            const errors: FileError[] = [];

            if (file.size > FILE_MAX_SIZE_IN_BYTES) {
                errors.push({
                    message: '', // We don't use these messages in the UI
                    code: 'file-too-large',
                });
            }
            if (!isFileTypeAccepted(file)) {
                errors.push({
                    message: '', // We don't use these messages in the UI
                    code: 'file-invalid-type',
                });
            }
            if (errors.length > 0) {
                rejectedFiles.push({ file, errors });
            } else {
                acceptedFiles.push(file);
            }
        });

        onUpload(acceptedFiles, rejectedFiles);
    };
    return (
        <AppBar
            position="fixed"
            color="default"
            sx={{
                top: 'auto',
                bottom: 0,
                backgroundColor: 'white',
                borderTop: 1,
                borderColor: 'divider',
            }}
        >
            <Toolbar>
                <StyledButton
                    component="label"
                    role={undefined}
                    variant="primary"
                    size="large"
                    tabIndex={-1}
                    fullWidth
                    disabled={isPending}
                    startIcon={isPending ? <Spinner /> : undefined}
                >
                    <>
                        {isPending ? 'Uploading...' : 'Upload'}
                        <VisuallyHiddenInput
                            type="file"
                            multiple
                            accept={Object.keys(ACCEPTED_FILE_TYPES).join(', ')}
                            onChange={handleChange}
                        />
                    </>
                </StyledButton>
            </Toolbar>
        </AppBar>
    );
};

export default MobileUpload;
